/* =========================================================================
   Change Order — Detail (v2)
   Ported from docs/featureBriefs/design_handoff_work_orders_change_orders/prototype/app/work-orders.js
   (PAGES["co-detail"] + the CO-related shared modals).
   ========================================================================= */
import React, { useCallback, useEffect, useState } from "react";
import {
    AlertTriangle,
    ArrowRight,
    Ban,
    Check,
    CheckCircle2,
    ChevronRight,
    ClipboardList,
    Clock,
    Eye,
    FileText,
    Hash,
    Layers,
    MoreHorizontal,
    Plus,
    Trash2,
    XCircle,
} from "lucide-react";
import "./WorkOrders.css";
import { useSiteContext } from "../../../SiteContext";
import { Page } from "../../../interfaces/PagesInterface";
import ChangeOrdersManager from "../../../managers/ChangeOrdersManager";
import WorkOrdersManager from "../../../managers/WorkOrdersManager";
import { openChangeOrderPdf, openChangeOrderPdfVersion } from "../../../api/changeOrders";
import {
    ChangeOrderDetail,
    ChangeOrderHistory,
    ChangeOrderMember,
    ChangeOrderStatus,
} from "../../../interfaces/ChangeOrderInterface";
import { WorkOrderListItem } from "../../../interfaces/WorkOrderInterface";
import {
    BackBar,
    Banner,
    CO_STATUS_META,
    Ck,
    DocRow,
    EmptyState,
    Gate,
    Kv,
    ModalShell,
    OverflowMenu,
    PageHead,
    SectionLabel,
    StatusPill,
    Timeline,
    TimelineEntry,
    TradeChip,
    fmtDate,
    fmtDateTime,
    money,
    useToast,
    woNumText,
} from "./woShared";

const CO_NUMBER_PATTERN = /^[A-Za-z0-9 #]+$/;

/** Mirrors the legacy detail page's history-title derivation (fromStatus ===
 *  null → created; same-status entries are WO add/remove/release notes;
 *  otherwise the status transition itself names the entry). */
function historyTitle(entry: ChangeOrderHistory): string {
    const woSuffix = entry.relatedWorkOrderNumber ? ` — WO #${entry.relatedWorkOrderNumber}` : "";
    if (entry.fromStatus == null) return "Change Order Created";
    if (entry.fromStatus === entry.toStatus) {
        if (entry.notes?.startsWith("WO added")) return `Work Order Added${woSuffix}`;
        if (entry.notes?.startsWith("WO removed")) return `Work Order Removed${woSuffix}`;
        if (entry.notes?.startsWith("WO released")) return `Work Order Released${woSuffix}`;
        return "Updated";
    }
    if (entry.toStatus === ChangeOrderStatus.Completed) return "Marked Completed";
    if (entry.toStatus === ChangeOrderStatus.Voided) return "Voided";
    if (entry.toStatus === ChangeOrderStatus.NoCharge) return "Marked No Charge";
    return "Updated";
}

type ModalKind = "complete" | "void" | "noCharge" | "addCO" | "remove" | "addWOs" | "renumber" | null;

const ChangeOrderDetailPage: React.FC = () => {
    const { setPage } = useSiteContext();
    const { toastNode, showToast } = useToast();

    const [co, setCo] = useState<ChangeOrderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [expandedID, setExpandedID] = useState<number | null>(null);

    const [notesDraft, setNotesDraft] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);
    const [notesError, setNotesError] = useState<string | null>(null);

    const [modal, setModal] = useState<ModalKind>(null);
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [corDraft, setCorDraft] = useState("");
    const [reasonDraft, setReasonDraft] = useState("");

    const [coTargets, setCoTargets] = useState<ChangeOrderMember[]>([]);
    const [coDrafts, setCoDrafts] = useState<Record<number, string>>({});

    const [removeTarget, setRemoveTarget] = useState<ChangeOrderMember | null>(null);

    const [addWOEligible, setAddWOEligible] = useState<WorkOrderListItem[]>([]);
    const [addWOLoading, setAddWOLoading] = useState(false);
    const [addWOSelected, setAddWOSelected] = useState<Set<number>>(new Set());

    const fetchCO = useCallback(async () => {
        const id = ChangeOrdersManager.selectedChangeOrderID;
        if (!id) {
            setError("No change order selected.");
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await ChangeOrdersManager.getChangeOrder(id);
            setCo(data);
            setNotesDraft(data?.notes ?? "");
        } catch (err) {
            console.error("Error fetching change order", err);
            setError("Failed to load change order.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCO(); }, [fetchCO]);

    if (loading && !co) {
        return (
            <div className="wov2">
                <BackBar label="Change Orders" onBack={() => setPage(Page.ChangeOrders)} />
                <div className="muted" style={{ padding: 40, textAlign: "center" }}>Loading change order…</div>
            </div>
        );
    }

    if (error && !co) {
        return (
            <div className="wov2">
                <BackBar label="Change Orders" onBack={() => setPage(Page.ChangeOrders)} />
                <Banner
                    variant="danger"
                    Icon={AlertTriangle}
                    action={<button className="btn sm" onClick={fetchCO} type="button">Retry</button>}
                >
                    {error}
                </Banner>
            </div>
        );
    }

    if (!co) return null;

    const members = co.members ?? [];
    const missingMembers = members.filter((m) => !(m.coNumber && m.coNumber.trim()));
    const missing = missingMembers.length;
    const allHaveCO = members.length > 0 && missing === 0;
    const amount = members.reduce((sum, m) => sum + (m.totalAmount ?? 0), 0);
    const open = co.status === ChangeOrderStatus.Open;

    const goToWorkOrder = (workOrderID: number) => {
        WorkOrdersManager.selectedWorkOrderID = workOrderID;
        setPage(Page.WorkOrderDetail);
    };

    const closeModal = () => { if (!modalSubmitting) setModal(null); };

    const openSimpleModal = (kind: Exclude<ModalKind, null>) => {
        setModalError(null);
        setReasonDraft("");
        setModal(kind);
    };

    const openAddCOModal = (single?: ChangeOrderMember) => {
        const targets = single ? [single] : missingMembers;
        const drafts: Record<number, string> = {};
        targets.forEach((w) => { drafts[w.workOrderID] = w.coNumber ?? ""; });
        setCoTargets(targets);
        setCoDrafts(drafts);
        setModalError(null);
        setModal("addCO");
    };

    const openRemoveModal = (m: ChangeOrderMember) => {
        setRemoveTarget(m);
        setModalError(null);
        setModal("remove");
    };

    const openAddWOsModal = async () => {
        setModal("addWOs");
        setModalError(null);
        setAddWOSelected(new Set());
        setAddWOLoading(true);
        try {
            const data = await ChangeOrdersManager.getEligibleWorkOrders(co.jobNumber);
            setAddWOEligible(data || []);
        } catch (err) {
            console.error("Error fetching eligible work orders", err);
            setAddWOEligible([]);
            setModalError("Failed to load eligible work orders.");
        } finally {
            setAddWOLoading(false);
        }
    };

    // TAS-304 follow-up: renumber an open CO; server enforces the duplicate check.
    const handleRenumber = async () => {
        const n = Number(corDraft);
        if (!Number.isInteger(n) || n < 1) return;
        setModalSubmitting(true);
        setModalError(null);
        try {
            await ChangeOrdersManager.updateCORNumber(co.changeOrderID, n);
            setModal(null);
            showToast(`Renumbered to COR #${n}.`);
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to update COR number.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleComplete = async () => {
        setModalSubmitting(true);
        setModalError(null);
        try {
            await ChangeOrdersManager.markCompleted(co.changeOrderID);
            setModal(null);
            showToast("Change order completed.");
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to complete change order.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleVoid = async () => {
        if (!reasonDraft.trim()) return;
        setModalSubmitting(true);
        setModalError(null);
        try {
            await ChangeOrdersManager.voidChangeOrder(co.changeOrderID, reasonDraft.trim());
            setModal(null);
            showToast("Change order voided — members released.");
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to void change order.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleNoCharge = async () => {
        if (!reasonDraft.trim()) return;
        setModalSubmitting(true);
        setModalError(null);
        try {
            await ChangeOrdersManager.markNoCharge(co.changeOrderID, reasonDraft.trim());
            setModal(null);
            showToast("Change order marked No Charge — members released.");
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to mark change order as No Charge.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const coDraftValid = (v: string) => {
        const trimmed = v.trim();
        return trimmed.length > 0 && trimmed.length <= 50 && CO_NUMBER_PATTERN.test(trimmed);
    };
    const canSaveCO = coTargets.length > 0 && coTargets.every((w) => coDraftValid(coDrafts[w.workOrderID] ?? ""));

    const handleSaveCO = async () => {
        if (!canSaveCO) return;
        setModalSubmitting(true);
        setModalError(null);
        try {
            const values = coTargets.map((w) => (coDrafts[w.workOrderID] ?? "").trim());
            const allSame = values.length > 1 && values.every((v) => v === values[0]);
            if (allSame) {
                await ChangeOrdersManager.setWorkOrderCONumbers(co.changeOrderID, coTargets.map((w) => w.workOrderID), values[0]);
            } else {
                await Promise.all(coTargets.map((w) =>
                    ChangeOrdersManager.setWorkOrderCONumber(w.workOrderID, (coDrafts[w.workOrderID] ?? "").trim())
                ));
            }
            setModal(null);
            showToast("CO# saved.");
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to save CO#.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleRemove = async () => {
        if (!removeTarget) return;
        setModalSubmitting(true);
        setModalError(null);
        try {
            await ChangeOrdersManager.removeWorkOrder(co.changeOrderID, removeTarget.workOrderID);
            setModal(null);
            setRemoveTarget(null);
            showToast("Work order removed from change order.");
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to remove work order.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const toggleAddWO = (id: number) => {
        setAddWOSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleAddWOs = async () => {
        if (addWOSelected.size === 0) return;
        setModalSubmitting(true);
        setModalError(null);
        try {
            await ChangeOrdersManager.addWorkOrders(co.changeOrderID, Array.from(addWOSelected));
            setModal(null);
            showToast("Work orders added.");
            await fetchCO();
        } catch (err: any) {
            setModalError(err?.response?.data?.message || err?.message || "Failed to add work orders.");
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleSaveNotes = async () => {
        setSavingNotes(true);
        setNotesError(null);
        try {
            await ChangeOrdersManager.updateNotes(co.changeOrderID, notesDraft.trim());
            showToast("Notes saved.");
            await fetchCO();
        } catch (err: any) {
            setNotesError(err?.response?.data?.message || err?.message || "Failed to save notes.");
        } finally {
            setSavingNotes(false);
        }
    };

    const handleViewPdf = async () => {
        try {
            await openChangeOrderPdf(co.changeOrderID);
        } catch (err: any) {
            setNotesError(err?.message || "Failed to open PDF.");
        }
    };

    const handleViewPdfVersion = async (versionNumber: number) => {
        try {
            await openChangeOrderPdfVersion(co.changeOrderID, versionNumber);
        } catch (err: any) {
            setNotesError(err?.message || "Failed to open PDF version.");
        }
    };

    const notesDirty = notesDraft.trim() !== (co.notes ?? "").trim();

    const timelineEntries: TimelineEntry[] = (co.history ?? []).map((h) => {
        const meta = CO_STATUS_META[h.toStatus] ?? CO_STATUS_META[1];
        return {
            key: h.changeOrderHistoryID,
            stClass: meta.stClass,
            Icon: meta.Icon,
            title: historyTitle(h),
            note: h.notes,
            when: fmtDateTime(h.createdAt),
            by: h.changedByName,
        };
    });

    return (
        <div className="wov2">
            <BackBar
                label="Change Orders"
                onBack={() => setPage(Page.ChangeOrders)}
                crumb={<><b>COR #{co.corNumber}</b> · Job #{co.jobNumber} · {co.projectName}</>}
            />
            <PageHead Icon={Layers} title={`COR #${co.corNumber}`} sub={`Job #${co.jobNumber} · ${co.projectName}`} />

            {co.status === ChangeOrderStatus.Completed && (
                <Banner variant="ok" Icon={CheckCircle2}>
                    <b>Completed.</b> All {members.length} member work orders carry a CO#. This change order is read-only.
                </Banner>
            )}
            {co.status === ChangeOrderStatus.Voided && (
                <Banner variant="danger" Icon={XCircle}>
                    <b>Voided {fmtDate(co.voidedAt)}.</b> {co.voidReason} <b>Change-order closure is final.</b>
                </Banner>
            )}
            {co.status === ChangeOrderStatus.NoCharge && (
                <Banner variant="neutral" Icon={Ban}>
                    <b>No Charge {fmtDate(co.noChargeAt)}.</b> {co.noChargeReason} <b>Closure is final.</b>
                </Banner>
            )}

            {open && (
                <div className="actionrow stack">
                    <div className="actionrow-btns">
                        <button className="btn primary" disabled={!allHaveCO} onClick={() => openSimpleModal("complete")} type="button">
                            <CheckCircle2 size={15} />Mark Completed
                        </button>
                        <button className="btn" onClick={openAddWOsModal} type="button">
                            <Plus size={15} />Add Work Orders
                        </button>
                        {missing > 0 && (
                            <button className="btn" onClick={() => openAddCOModal()} type="button">
                                <Hash size={15} />Add CO# ({missing})
                            </button>
                        )}
                        <span className="grow" />
                        <OverflowMenu
                            ButtonIcon={MoreHorizontal}
                            items={[
                                { label: "Mark as No Charge", Icon: Ban, danger: true, onClick: () => openSimpleModal("noCharge") },
                                "sep",
                                { label: "Void change order", Icon: XCircle, danger: true, onClick: () => openSimpleModal("void") },
                            ]}
                        />
                    </div>
                    {!allHaveCO && (
                        <Gate>
                            <b>Mark Completed is locked.</b> {missing} of {members.length} member work order{missing === 1 ? "" : "s"} still {missing === 1 ? "needs" : "need"} a CO# from the builder. Use <b>Add CO#</b> to enter them, then complete.
                        </Gate>
                    )}
                </div>
            )}

            <div className="kv-grid">
                <Kv label="COR Number">
                    <span className="mono">COR #{co.corNumber}</span>
                    {co.status === 1 && (
                        <button
                            className="linkbtn"
                            style={{ marginLeft: 8 }}
                            onClick={() => { setCorDraft(String(co.corNumber)); setModalError(null); setModal("renumber"); }}
                            type="button"
                        >
                            Edit
                        </button>
                    )}
                </Kv>
                <Kv label="Job Number"><span className="mono">#{co.jobNumber}</span></Kv>
                <Kv label="Project">{co.projectName || "–"}</Kv>
                <Kv label="Status"><StatusPill kind="co" status={co.status} /></Kv>
                <Kv label="Work Orders">{members.length}</Kv>
                <Kv label="Total Amount"><span className="mono">{money(amount)}</span></Kv>
                <Kv label="Created">{fmtDate(co.createdAt)}</Kv>
            </div>

            <SectionLabel Icon={ClipboardList} style={{ marginTop: 24 }}>Member Work Orders</SectionLabel>
            <div className="card">
                <div className="card-head">
                    <ClipboardList size={16} />
                    <h3>Member Work Orders</h3>
                    <span className="ch-sub">{members.length} · {money(amount)}</span>
                    {open && missing > 0 && (
                        <span className="right">
                            <button className="btn sm" onClick={() => openAddCOModal()} type="button">
                                <Hash size={13} />Add CO# to {missing}
                            </button>
                        </span>
                    )}
                </div>
                <div className="tbl-wrap">
                    <table className="dt">
                        <thead>
                            <tr>
                                <th className="cen" style={{ width: 34 }}></th>
                                <th>WO #</th>
                                <th>Description</th>
                                <th className="num">Amount</th>
                                <th>CO #</th>
                                <th className="cen">{open ? "Actions" : "Detail"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.length ? members.map((m) => {
                                const isOpenRow = expandedID === m.workOrderID;
                                const hasCO = !!(m.coNumber && m.coNumber.trim());
                                return (
                                    <React.Fragment key={m.workOrderID}>
                                        <tr
                                            className={`exp-head ${isOpenRow ? "open" : ""}`}
                                            onClick={() => setExpandedID(isOpenRow ? null : m.workOrderID)}
                                        >
                                            <td className="cen"><ChevronRight size={14} className="exp-ic" /></td>
                                            <td className="strong"><span className="code">{woNumText(m)}</span></td>
                                            <td>{m.firstItemDescription ?? "–"}</td>
                                            <td className="num strong">{money(m.totalAmount)}</td>
                                            <td>{hasCO ? <span className="code">{m.coNumber}</span> : <span className="co-missing">needs CO#</span>}</td>
                                            <td className="cen" onClick={(e) => e.stopPropagation()}>
                                                {open ? (
                                                    <div className="row-act">
                                                        <button className="linkbtn" onClick={() => openAddCOModal(m)} type="button">
                                                            {hasCO ? "Edit" : "Add CO#"}
                                                        </button>
                                                        <button className="linkbtn danger" onClick={() => openRemoveModal(m)} type="button">Remove</button>
                                                    </div>
                                                ) : (
                                                    <button className="linkbtn" onClick={() => goToWorkOrder(m.workOrderID)} type="button">Open →</button>
                                                )}
                                            </td>
                                        </tr>
                                        {isOpenRow && (
                                            <tr className="exp-body">
                                                <td colSpan={6}>
                                                    <div className="exp-inner">
                                                        <div><div className="ek">Employee</div><div className="ev">{m.employeeName}</div></div>
                                                        <div><div className="ek">Supervisor</div><div className="ev">{m.wcdpSupervisorName ?? "–"}</div></div>
                                                        <div><div className="ek">Status</div><div className="ev"><StatusPill kind="wo" status={m.status} /></div></div>
                                                        <div><div className="ek">Trade</div><div className="ev"><TradeChip trade={m.trade} /></div></div>
                                                        <div><div className="ek">PO #</div><div className="ev">{m.purchaseOrderNumber ? <span className="mono">{m.purchaseOrderNumber}</span> : "—"}</div></div>
                                                        <div><div className="ek">Hours</div><div className="ev mono">{(m.totalHours ?? 0).toFixed(1)}</div></div>
                                                        <div className="full">
                                                            <div className="ek">Line items ({m.itemCount})</div>
                                                            <div className="ev" style={{ fontWeight: 500 }}>
                                                                {m.firstItemDescription ?? "–"}{m.itemCount > 1 ? ` +${m.itemCount - 1} more` : ""}
                                                            </div>
                                                        </div>
                                                        <div className="full">
                                                            <button className="btn sm" onClick={() => goToWorkOrder(m.workOrderID)} type="button">
                                                                <ArrowRight size={13} />Open full work order
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            }) : (
                                <tr><td colSpan={6}>
                                    <EmptyState Icon={ClipboardList} style={{ padding: 30 }}>
                                        No member work orders — this change order released them on closure.
                                    </EmptyState>
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <SectionLabel Icon={FileText}>Notes</SectionLabel>
            <div className="card pad" style={{ marginBottom: 6 }}>
                <textarea
                    className="input"
                    disabled={!open}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Add notes…"
                />
                {notesError && <Banner variant="danger" Icon={AlertTriangle}>{notesError}</Banner>}
                {open && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                        <button
                            className="btn sm primary"
                            disabled={!notesDirty || savingNotes}
                            onClick={handleSaveNotes}
                            type="button"
                        >
                            <Check size={13} />{savingNotes ? "Saving…" : "Save Notes"}
                        </button>
                    </div>
                )}
            </div>

            {/* The prototype overrides the shared .dash-two ratio (1.15fr/.85fr, used
                by the WO detail footer) to an even 1fr/1fr split for this page. */}
            <div className="dash-two" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 6 }}>
                <div>
                    <SectionLabel Icon={Clock}>History</SectionLabel>
                    <div className="card pad"><Timeline entries={timelineEntries} /></div>
                </div>
                <div>
                    <SectionLabel Icon={FileText}>Documents</SectionLabel>
                    <div className="card pad">
                        <DocRow
                            Icon={FileText}
                            name="Combined change order PDF"
                            meta={`All ${members.length} member work orders · ${fmtDate(co.createdAt)}`}
                            action={<button className="btn sm" onClick={handleViewPdf} type="button"><Eye size={13} />View</button>}
                        />
                        {(co.pdfVersions ?? []).map((v) => (
                            <DocRow
                                key={v.changeOrderPdfVersionID}
                                Icon={FileText}
                                name={`Version ${v.versionNumber}`}
                                meta={`${v.generatedReason} · ${fmtDate(v.createdAt)}`}
                                action={<button className="btn sm" onClick={() => handleViewPdfVersion(v.versionNumber)} type="button"><Eye size={13} />View</button>}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {modal === "renumber" && (
                <ModalShell
                    Icon={Layers}
                    title="Edit COR Number"
                    sub={`Job #${co.jobNumber} · currently COR #${co.corNumber}`}
                    onClose={closeModal}
                    footer={
                        <>
                            <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting}>Cancel</button>
                            <button
                                className="btn primary"
                                onClick={handleRenumber}
                                disabled={modalSubmitting || !corDraft.trim() || Number(corDraft) === co.corNumber}
                            >
                                {modalSubmitting ? "Saving…" : "Save COR number"}
                            </button>
                        </>
                    }
                >
                    <div className="field">
                        <label>COR Number</label>
                        <input
                            className="input"
                            autoFocus
                            inputMode="numeric"
                            value={corDraft}
                            onChange={(e) => setCorDraft(e.target.value.replace(/[^0-9]/g, ""))}
                            disabled={modalSubmitting}
                        />
                        <span className="sub" style={{ marginTop: 4 }}>
                            Must not already exist on job #{co.jobNumber}. The change order PDF regenerates with the new number.
                        </span>
                    </div>
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {modal === "complete" && (
                <ModalShell
                    Icon={CheckCircle2}
                    title="Mark change order Completed?"
                    sub={`COR #${co.corNumber} · all members carry a CO#`}
                    onClose={closeModal}
                    footer={<>
                        <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting} type="button">Cancel</button>
                        <button className="btn primary" disabled={modalSubmitting} onClick={handleComplete} type="button">
                            {modalSubmitting ? "Working…" : "Mark Completed"}
                        </button>
                    </>}
                >
                    <Banner variant="ok" Icon={CheckCircle2}>
                        All {members.length} member work orders have a CO#. Completing is final and makes the change order read-only.
                    </Banner>
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {modal === "void" && (
                <ModalShell
                    Icon={XCircle}
                    danger
                    title="Void this change order?"
                    sub="This is destructive and final"
                    onClose={closeModal}
                    footer={<>
                        <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting} type="button">Cancel</button>
                        <button className="btn danger" disabled={modalSubmitting || !reasonDraft.trim()} onClick={handleVoid} type="button">
                            {modalSubmitting ? "Working…" : "Void & release work orders"}
                        </button>
                    </>}
                >
                    <Banner variant="warn" Icon={AlertTriangle}>
                        <b>Voiding releases all {members.length} member work orders.</b> They return to their own status and become free to add to a different change order. Change-order closure cannot be undone.
                    </Banner>
                    <div className="field">
                        <label>Reason <span style={{ color: "var(--danger)" }}>*</span></label>
                        <textarea
                            className="input"
                            value={reasonDraft}
                            onChange={(e) => setReasonDraft(e.target.value)}
                            placeholder="Why is this change order being voided?"
                            autoFocus
                        />
                    </div>
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {modal === "noCharge" && (
                <ModalShell
                    Icon={Ban}
                    danger
                    title="Mark change order as No Charge?"
                    sub="This is destructive and final"
                    onClose={closeModal}
                    footer={<>
                        <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting} type="button">Cancel</button>
                        <button className="btn danger" disabled={modalSubmitting || !reasonDraft.trim()} onClick={handleNoCharge} type="button">
                            {modalSubmitting ? "Working…" : "Mark No Charge"}
                        </button>
                    </>}
                >
                    <Banner variant="warn" Icon={AlertTriangle}>
                        <b>This releases all {members.length} member work orders</b> and closes the change order permanently.
                    </Banner>
                    <div className="field">
                        <label>Reason <span style={{ color: "var(--danger)" }}>*</span></label>
                        <textarea
                            className="input"
                            value={reasonDraft}
                            onChange={(e) => setReasonDraft(e.target.value)}
                            placeholder="Why is this no charge?"
                            autoFocus
                        />
                    </div>
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {modal === "addCO" && (
                <ModalShell
                    Icon={Hash}
                    title={coTargets.length === 1 ? "Add CO#" : `Add CO# to ${coTargets.length} work orders`}
                    sub={`COR #${co.corNumber} · Job #${co.jobNumber}`}
                    onClose={closeModal}
                    wide
                    footer={<>
                        <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting} type="button">Cancel</button>
                        <button className="btn primary" disabled={modalSubmitting || !canSaveCO} onClick={handleSaveCO} type="button">
                            {modalSubmitting ? "Working…" : coTargets.length === 1 ? "Save CO#" : `Save ${coTargets.length} CO#s`}
                        </button>
                    </>}
                >
                    <Banner variant="info" Icon={Hash}>
                        Enter the builder's CO number for {coTargets.length === 1 ? "this work order" : "each member below"}. Format: letters, numbers, spaces and #.
                    </Banner>
                    {coTargets.map((w) => (
                        <div className="field" key={w.workOrderID}>
                            <label>{woNumText(w)} — {w.firstItemDescription ?? ""}</label>
                            <input
                                className="input"
                                value={coDrafts[w.workOrderID] ?? ""}
                                onChange={(e) => setCoDrafts((prev) => ({ ...prev, [w.workOrderID]: e.target.value }))}
                                placeholder="e.g. CH-8821-14"
                                maxLength={50}
                            />
                        </div>
                    ))}
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {modal === "remove" && removeTarget && (
                <ModalShell
                    Icon={Trash2}
                    danger
                    title="Remove from change order?"
                    sub={woNumText(removeTarget)}
                    onClose={closeModal}
                    footer={<>
                        <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting} type="button">Cancel</button>
                        <button className="btn danger" disabled={modalSubmitting} onClick={handleRemove} type="button">
                            {modalSubmitting ? "Working…" : "Remove work order"}
                        </button>
                    </>}
                >
                    <p style={{ margin: 0, color: "var(--text-2)", fontSize: 14 }}>
                        This releases <b>{woNumText(removeTarget)}</b> from the change order. Its own status is unaffected and it becomes eligible for another change order.
                    </p>
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {modal === "addWOs" && (
                <ModalShell
                    Icon={Plus}
                    title="Add Work Orders"
                    sub={`Eligible on Job #${co.jobNumber}`}
                    onClose={closeModal}
                    wide
                    footer={<>
                        <button className="btn ghost" onClick={closeModal} disabled={modalSubmitting} type="button">Cancel</button>
                        <button className="btn primary" disabled={modalSubmitting || addWOSelected.size === 0} onClick={handleAddWOs} type="button">
                            {modalSubmitting ? "Working…" : "Add selected"}
                        </button>
                    </>}
                >
                    {addWOLoading ? (
                        <div className="muted" style={{ padding: 20, textAlign: "center" }}>Loading eligible work orders…</div>
                    ) : addWOEligible.length ? (
                        addWOEligible.map((w) => (
                            <div
                                key={w.workOrderID}
                                className={`pick-row ${addWOSelected.has(w.workOrderID) ? "on" : ""}`}
                                onClick={() => toggleAddWO(w.workOrderID)}
                            >
                                <Ck on={addWOSelected.has(w.workOrderID)} onClick={(e) => { e.stopPropagation(); toggleAddWO(w.workOrderID); }} />
                                <div className="grow">
                                    <div className="pr-t">{woNumText(w)} — {w.firstItemDescription ?? ""}</div>
                                    <div className="pr-s">{w.employeeName} · {w.projectName}</div>
                                </div>
                                <span className="strong mono">{money(w.totalAmount)}</span>
                            </div>
                        ))
                    ) : (
                        <EmptyState Icon={AlertTriangle} style={{ padding: 24 }}>
                            No eligible work orders on this job — all are voided, no-charge, or already in a change order.
                        </EmptyState>
                    )}
                    {modalError && <Banner variant="danger" Icon={AlertTriangle}>{modalError}</Banner>}
                </ModalShell>
            )}

            {toastNode}
        </div>
    );
};

export default ChangeOrderDetailPage;
